import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
} from '@angular/common/http';
import { NEVER, Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { BasicDialogComponent } from '../shared/components/basic-dialog/basic-dialog.component';
import { fetchAuthSession } from 'aws-amplify/auth';
import { SimpleDialog } from '../shared/models/simple-dialog';
import { environment } from '../../environments/environment';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  constructor(
    private dialog: MatDialog,
    public router: Router,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const originalUrl = req.url;
    const simpleDialog: SimpleDialog = {
      uiTitle: 'Your session has expired',
      uiMessage:
        "To continue, please click 'OK' to be redirected to the login page.",
      redirect: '/auth/logout',
    };

    // Skip token processing for non-API requests
    if (!originalUrl.includes(`${environment.apiGatewayUrl}`)) {
      console.log('Non-API request, proceeding without token');

      return next.handle(req);
    }

    // Convert Promise to Observable and use switchMap to wait for completion
    // catchError is placed BEFORE switchMap to only catch fetchAuthSession errors,
    // not HTTP response errors (which would cause duplicate requests)
    return from(fetchAuthSession()).pipe(
      catchError((error) => {
        console.error('Error fetching auth session:', error);

        // If it's a login request or other auth endpoint, allow without token
        if (originalUrl.includes('/auth/') || originalUrl.includes('/login')) {
          console.log('Auth endpoint request, proceeding without token');
          return next.handle(req);
        }

        // For other endpoints, we might want to redirect to login
        // or allow the request to proceed and let the backend handle auth errors
        console.warn('Proceeding with request without authentication');
        return next.handle(req);
      }),
      switchMap((session: any) => {
        const accessToken = session.tokens?.accessToken?.toString();
        const idToken = session.tokens?.idToken;
        const tokenExpirationTime = idToken?.payload.exp;
        const now = Math.floor(Date.now() / 1000);

        // Check for token expiration
        if (tokenExpirationTime && tokenExpirationTime <= now) {
          console.log('Token expired, showing dialog');
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return NEVER;
        }

        // Add the token to the request
        if (accessToken) {
          const modifiedReq = req.clone({
            headers: req.headers.set('Authorization', `Bearer ${accessToken}`),
          });
          return next.handle(modifiedReq);
        } else {
          console.warn('No access token available, proceeding without token');
          this.dialog.open(BasicDialogComponent, { data: simpleDialog });
          return next.handle(req);
        }
      }),
    );
  }
}
